import { z } from 'zod';
import { NpcId } from './enums';

/**
 * 上班小遊戲 (shift games). Every job has its own game; what a shift is made of that day (who
 * comes in, what they say, the interview, the brief…) is a ShiftPack from the server: written
 * fresh each day by AI for the text-heavy jobs, built from authored pools and the day's seed
 * otherwise. The game plays it and hands back a 0–100 score; the server grades it and applies
 * the pack's people (who you served) to your relationships, so the pack is never the client's word.
 * The rules a pack must keep to be playable live here, so authored, AI and client packs are all
 * held to the same ones.
 */

/** Higher ranks are harder, with a little more time each: 45 s at Lv.1, +5 s a level. */
export const shiftSeconds = (level: number) => 45 + 5 * (Math.max(1, level) - 1);

// ---------- shared vocabularies (contract, AI schema and authored pools) ----------

export const PetKind = z.enum(['cat', 'dog', 'rabbit', 'hamster', 'bird']);
export type PetKind = z.infer<typeof PetKind>;
/** The care a clinic patient needs: settle it, weigh it, reward it, or fetch Grace. */
export const VetNeed = z.enum(['calm', 'scale', 'treat', 'vet']);
export type VetNeed = z.infer<typeof VetNeed>;
/** What a late-night caller needs: a soft song, an upbeat one, an oldie, or just to be heard. */
export const RadioWants = z.enum(['soft', 'upbeat', 'old', 'listen']);
export type RadioWants = z.infer<typeof RadioWants>;
export const Bread = z.enum(['croissant', 'milk_bread', 'egg_tart', 'pineapple_bun']);
export type Bread = z.infer<typeof Bread>;

// ---------- headlines (校刊記者) ----------

const CJK = /[぀-ヿ㐀-鿿豈-﫿]/;
/** Characters as a reader counts them (a CJK character is one, not two UTF-16 units; spaces count). */
export const charCount = (text: string) => [...text].length;
/** Which way a headline's tiles join: Chinese tiles run straight on, English ones take a space. */
export const headlineLang = (tiles: readonly { text: string }[]): 'zh' | 'en' => (tiles.some((x) => CJK.test(x.text)) ? 'zh' : 'en');
/** A headline from tiles in order, joined the way the game joins them. */
export const headlineText = (tiles: readonly { text: string }[], lang: 'zh' | 'en') =>
  tiles
    .map((x) => x.text)
    .join(lang === 'en' ? ' ' : '')
    .trim();

// ---------- per-job content ----------

/** 咖啡師: orders built from steps; regulars only say 「老樣子」 and the recipe card holds their order. */
export const BaristaOrder = z.object({
  base: z.enum(['espresso', 'tea', 'matcha']),
  milk: z.enum(['none', 'milk', 'oat']),
  ice: z.boolean(),
  sweet: z.union([z.literal(0), z.literal(1), z.literal(2)]),
});
export type BaristaOrder = z.infer<typeof BaristaOrder>;
export const BaristaPack = z.object({
  kind: z.literal('barista'),
  customers: z.array(z.object({ npcId: NpcId, line: z.string(), order: BaristaOrder, regular: z.boolean() })).min(4),
  /** Shown before the shift: the regulars' usual orders (Chloe's recipe cards). */
  recipeCards: z.array(z.object({ npcId: NpcId, order: BaristaOrder })),
  /** The latte art to trace at closing: harder shapes at higher ranks. */
  latteArt: z.enum(['heart', 'tulip', 'rosetta']),
});

/** App 實習生: bugs are placed from `seed` (count by rank); then residents' feedback to triage, some urgent and some not. */
export const InternPack = z
  .object({
    kind: z.literal('intern'),
    seed: z.number().int(),
    bugCount: z.number().int().min(3).max(8),
    feedback: z.array(z.object({ npcId: NpcId, text: z.string(), fixToday: z.boolean(), why: z.string() })).min(4),
  })
  .superRefine((p, ctx) => {
    if (p.feedback.every((f) => f.fixToday) || p.feedback.every((f) => !f.fixToday)) ctx.addIssue({ code: 'custom', path: ['feedback'], message: 'feedback all one way' });
  });

/**
 * 校刊記者: tap the quotable lines in an interview, maybe pick a follow-up, then build the headline.
 * Some lines are quotable and some aren't; a follow-up comes after a real line with one good
 * question; and the good tiles, joined the way the game joins them, fit in `maxChars`.
 */
export const ReporterPack = z
  .object({
    kind: z.literal('reporter'),
    npcId: NpcId,
    topic: z.string(),
    lines: z.array(z.object({ text: z.string(), quote: z.boolean() })).min(5),
    followUp: z.object({ afterLine: z.number().int().min(0), question: z.string(), options: z.array(z.object({ text: z.string(), good: z.boolean() })).length(3) }).nullable(),
    headline: z.object({ tiles: z.array(z.object({ text: z.string(), good: z.boolean() })).min(5), maxChars: z.number().int().min(6), section: z.string() }),
  })
  .superRefine((p, ctx) => {
    const quotes = p.lines.filter((l) => l.quote).length;
    if (quotes < 2 || quotes === p.lines.length) ctx.addIssue({ code: 'custom', path: ['lines'], message: 'quotes: at least two, and not every line' });
    if (p.followUp && p.followUp.afterLine >= p.lines.length) ctx.addIssue({ code: 'custom', path: ['followUp', 'afterLine'], message: 'follow-up after a line that does not exist' });
    if (p.followUp && p.followUp.options.filter((o) => o.good).length !== 1) ctx.addIssue({ code: 'custom', path: ['followUp', 'options'], message: 'follow-up needs exactly one good option' });
    const good = p.headline.tiles.filter((x) => x.good);
    if (!good.length || charCount(headlineText(good, headlineLang(p.headline.tiles))) > p.headline.maxChars)
      ctx.addIssue({ code: 'custom', path: ['headline'], message: 'the good headline does not fit in maxChars' });
  });

/** 設計助手: a client's brief, palettes to choose from (exactly one fits), then lay the poster out. */
export const DesignerPack = z
  .object({
    kind: z.literal('designer'),
    npcId: NpcId,
    event: z.string(),
    brief: z.string(),
    tags: z.array(z.string()).min(2).max(4),
    palettes: z.array(z.object({ name: z.string(), colors: z.array(z.string().regex(/^#[0-9A-Fa-f]{6}$/)).length(4), fits: z.boolean() })).min(3),
    title: z.string(),
    when: z.string(),
  })
  .superRefine((p, ctx) => {
    if (p.palettes.filter((x) => x.fits).length !== 1) ctx.addIssue({ code: 'custom', path: ['palettes'], message: 'exactly one palette must fit' });
  });

/** 麵包店: bake to order — knead, shape, and pull each tray out at golden. */
export const BakeryPack = z.object({
  kind: z.literal('bakery'),
  orders: z.array(z.object({ npcId: NpcId, line: z.string(), bread: Bread, qty: z.number().int().min(1).max(4) })).min(3),
});

/** 婚禮攝影: frame the couple and catch the natural moment in each shot the lead asks for. */
export const PhotoPack = z.object({
  kind: z.literal('photo'),
  couple: z.string(),
  shots: z.array(z.object({ cue: z.string() })).min(3),
});

/** 海邊駐唱: sing the lines on the beat; listeners' requests between songs. */
export const SingerPack = z.object({
  kind: z.literal('singer'),
  song: z.object({ title: z.string(), lines: z.array(z.string()).min(4) }),
  requests: z.array(z.object({ npcId: NpcId, line: z.string() })).min(1),
});

/** 動物診所: calm each nervous patient, then give the care the owner's words point to. */
export const VetPack = z.object({
  kind: z.literal('vet'),
  patients: z
    .array(
      z.object({
        ownerNpcId: NpcId,
        species: PetKind,
        petName: z.string(),
        complaint: z.string(),
        need: VetNeed,
        /** 0 calm … 1 terrified: how long it takes to settle. */
        nervous: z.number().min(0).max(1),
      }),
    )
    .min(3),
});

/** 深夜電台: hear each caller out, pick what they need, and fade it in at the right moment. */
export const RadioPack = z.object({
  kind: z.literal('radio'),
  callers: z.array(z.object({ npcId: NpcId, line: z.string(), wants: RadioWants })).min(3),
});

const PACKS = [BaristaPack, InternPack, ReporterPack, DesignerPack, BakeryPack, PhotoPack, SingerPack, VetPack, RadioPack] as const;
export const ShiftContent = z.discriminatedUnion('kind', PACKS);
export type ShiftContent = z.infer<typeof ShiftContent>;
export type ShiftKind = ShiftContent['kind'];
/** Every kind of shift game. */
export const SHIFT_KINDS: readonly ShiftKind[] = PACKS.map((p) => p.shape.kind.value);
/** The pack for one kind of game. */
export type ShiftContentOf<K extends ShiftKind> = Extract<ShiftContent, { kind: K }>;

/** Which game each job plays, by career id (content). */
export const SHIFT_KIND: Readonly<Record<string, ShiftKind>> = {
  cafe_barista: 'barista',
  app_intern: 'intern',
  campus_reporter: 'reporter',
  design_assistant: 'designer',
  bakery_apprentice: 'bakery',
  photo_assistant: 'photo',
  seaside_singer: 'singer',
  vet_assistant: 'vet',
  radio_assistant: 'radio',
};
/** The game a job plays; undefined for a job without one. */
export const kindOf = (careerId: string): ShiftKind | undefined => (Object.prototype.hasOwnProperty.call(SHIFT_KIND, careerId) ? SHIFT_KIND[careerId] : undefined);

/** Where a shift's pack came from: 'ai' written today, 'authored' from the pool (AI off, or not ready yet). */
export const ShiftPackSource = z.enum(['ai', 'authored']);
export type ShiftPackSource = z.infer<typeof ShiftPackSource>;

/** GET /careers/:id/shift — today's shift for this job at your rank. */
export const ShiftPackView = z.object({
  careerId: z.string(),
  level: z.number().int(),
  seconds: z.number().int(),
  /** Send it back with the shift (WorkShiftRequest.packSource) so the server reads the same pack. */
  source: ShiftPackSource,
  content: ShiftContent,
  /** Everyone the pack mentions, for names and faces. */
  cast: z.record(z.string(), z.object({ name: z.string(), portraitUrl: z.string() })),
});
export type ShiftPackView = z.infer<typeof ShiftPackView>;
